













































































































import { Component, Vue } from 'vue-property-decorator'
import PageHeader from '@/components/PageHeader.vue'
import _ from 'lodash'
import http from '@/http'
import { AxiosResponse } from 'axios'
import IFundingRequestResponse from '@/modules/funding-request/funding-request-response.interface'
import IFundingRequestFieldResponse from '@/modules/funding-request-field/funding-request-field-response.interface'
import FundingRequestFieldModel from '@/modules/funding-request-field/funding-request-field.model'
import FundingRequestModel from '@/modules/funding-request/funding-request.model'
import { FieldTypes } from '@/components/Form/FieldTypes'
import { Getters } from '@/store/auth/getters'
import UserModel from '@/modules/user/user.model'
import permissions from '@/helpers/permissions'
import { Actions } from '@/store/global/actions'
import AreYouSureDialog from '@/components/Dialogs/AreYouSureDialog.vue'

const DSTI_STORAGE_KEY = 'dstiCooldowns'

const DSTI_COOLDOWN_MS = 5 * 60 * 1000

type DstiCooldownMap = Record<string, number>

@Component({
  components: {
    PageHeader,
  },
})
export default class Dsti extends Vue {
  dstiFields: FundingRequestFieldModel[] | null = null
  fundingRequest: FundingRequestModel | null = null
  user: UserModel = this.$store.getters[Getters.getUser]
  permissions = permissions
  dstiRecheckDisabled = false
  private dstiCooldownTimer: number | null = null
  private dstiCountdownInterval: number | null = null
  private dstiCooldownEndsAt: number | null = null
  private dstiCooldownRemainingMs: number | null = null

  async created(): Promise<void> {
    await this.loadFundingRequest()
    this.initDstiCooldownFromStorage()
  }

  beforeDestroy(): void {
    this.clearDstiCooldownTimer()
    this.clearDstiCountdownInterval()
  }

  get uniqueDstiFields(): FundingRequestFieldModel[] {
    if (!this.dstiFields) return []

    return _.uniqBy(this.dstiFields, 'key')
  }

  noInfoToString(fieldValue: any): any {
    if (!fieldValue && fieldValue !== 0) {
      return 'Nėra informacijos'
    }

    return fieldValue
  }

  checkDsti(): void {
    this.$store.dispatch(Actions.showDialog, {
      show: true,
      component: AreYouSureDialog,
      meta: {
        title: 'Tikrinti DSTI dar kartą?',
        text: 'Užklausa užtruks kelias minutes. Kol ji vyksta galite tęsti darbą ar uždaryti naršyklės langą.',
        onYes: () => this.requestDstiRecheck(),
      },
    })
  }

  requestDstiRecheck(): Promise<void> {
    if (!this.fundingRequest?.uuid) {
      return Promise.resolve()
    }

    return http.get(`dsti/request/${this.fundingRequest.uuid}`).then(() => {
      this.handleDstiRequested()
    })
  }

  get dstiCooldownFormatted(): string | null {
    if (this.dstiCooldownRemainingMs == null) return null

    const totalSeconds = Math.ceil(this.dstiCooldownRemainingMs / 1000)

    if (totalSeconds <= 0) return null

    const minutes = Math.floor(totalSeconds / 60)

    const seconds = totalSeconds % 60

    const mm = String(minutes).padStart(2, '0')

    const ss = String(seconds).padStart(2, '0')

    return `${mm}:${ss}`
  }

  isFieldBoth(fieldKey: string): boolean {
    return fieldKey === 'stableIncomeBoth' || fieldKey === 'unstableIncomeBoth' || fieldKey === 'totalIncomeBoth'
  }

  loadFundingRequest(): void {
    http.get(`/funding-requests/${this.$router.currentRoute.params.uuid}`).then(
      (
        response: AxiosResponse<{
          funding_request: IFundingRequestResponse
          dsti_fields: IFundingRequestFieldResponse[]
        }>,
      ) => {
        this.dstiFields = response.data.dsti_fields.map((item: IFundingRequestFieldResponse) =>
          new FundingRequestFieldModel().transform(item),
        )
        this.fundingRequest = new FundingRequestModel().transform(response.data.funding_request)
      },
    )
  }

  getValue(data: object | undefined, field: FundingRequestFieldModel): string | false {
    if (!data) return false

    if (field.type === 'json') return false

    if (field.type === FieldTypes.select) {
      const found = field.options.find((option: any) => option.value === _.get(data, field.key))

      return found ? found.label : _.get(data, field.key, false)
    }

    if (field.type === FieldTypes.checkbox) return _.get(data, field.key) ? 'Taip' : 'Ne'

    if (field.type === FieldTypes.number) return _.get(data, field.key, null)

    return _.get(data, field.key)
  }

  private handleDstiRequested(): void {
    this.dstiRecheckDisabled = true

    const { uuid } = this.$router.currentRoute.params

    const map = this.getDstiCooldownMap()

    const now = Date.now()

    map[uuid] = now

    const cleanedMap = this.cleanupOldDstiEntries(map, now)
    this.saveDstiCooldownMap(cleanedMap)

    this.scheduleDstiCooldownTimer(DSTI_COOLDOWN_MS)
  }

  private initDstiCooldownFromStorage(): void {
    const { uuid } = this.$router.currentRoute.params

    const map = this.getDstiCooldownMap()

    const lastRequested = map[uuid]

    if (!lastRequested) return

    const now = Date.now()

    const elapsed = now - lastRequested

    const remaining = DSTI_COOLDOWN_MS - elapsed

    if (remaining > 0) {
      this.dstiRecheckDisabled = true
      this.scheduleDstiCooldownTimer(remaining)
    } else {
      delete map[uuid]

      const cleanedMap = this.cleanupOldDstiEntries(map, now)
      this.saveDstiCooldownMap(cleanedMap)
      this.dstiRecheckDisabled = false
    }
  }

  private scheduleDstiCooldownTimer(delayMs: number): void {
    this.clearDstiCooldownTimer()

    this.dstiCooldownEndsAt = Date.now() + delayMs
    this.startDstiCountdown()

    const uuidAtScheduleTime = this.$router.currentRoute.params.uuid

    this.dstiCooldownTimer = window.setTimeout(() => {
      const currentUuid = this.$router.currentRoute.params.uuid

      if (currentUuid === uuidAtScheduleTime) {
        this.loadFundingRequest()
        this.dstiRecheckDisabled = false

        const map = this.getDstiCooldownMap()
        delete map[currentUuid]

        const cleanedMap = this.cleanupOldDstiEntries(map, Date.now())
        this.saveDstiCooldownMap(cleanedMap)
      }

      this.dstiCooldownEndsAt = null
      this.dstiCooldownRemainingMs = null
      this.clearDstiCountdownInterval()

      this.dstiCooldownTimer = null
    }, delayMs)
  }

  private clearDstiCooldownTimer(): void {
    if (this.dstiCooldownTimer !== null) {
      clearTimeout(this.dstiCooldownTimer)
      this.dstiCooldownTimer = null
    }
  }

  private startDstiCountdown(): void {
    this.clearDstiCountdownInterval()
    this.updateDstiRemaining()

    this.dstiCountdownInterval = window.setInterval(() => {
      this.updateDstiRemaining()
    }, 1000)
  }

  private clearDstiCountdownInterval(): void {
    if (this.dstiCountdownInterval !== null) {
      clearInterval(this.dstiCountdownInterval)
      this.dstiCountdownInterval = null
    }
  }

  private updateDstiRemaining(): void {
    if (!this.dstiCooldownEndsAt) {
      this.dstiCooldownRemainingMs = null

      return
    }

    const remaining = this.dstiCooldownEndsAt - Date.now()
    this.dstiCooldownRemainingMs = remaining > 0 ? remaining : 0

    // Optional: if for some reason time passed without the timeout firing,
    // stop countdown when <= 0
    if (remaining <= 0) {
      this.clearDstiCountdownInterval()
    }
  }

  private getDstiCooldownMap(): DstiCooldownMap {
    if (typeof window === 'undefined') return {}

    try {
      const raw = window.localStorage.getItem(DSTI_STORAGE_KEY)

      if (!raw) return {}

      const parsed = JSON.parse(raw)

      if (!parsed || typeof parsed !== 'object') return {}

      return parsed as DstiCooldownMap
    } catch (e) {
      return {}
    }
  }

  private saveDstiCooldownMap(map: DstiCooldownMap): void {
    if (typeof window === 'undefined') return

    try {
      window.localStorage.setItem(DSTI_STORAGE_KEY, JSON.stringify(map))
    } catch (e) {
      console.error(e)
    }
  }

  private cleanupOldDstiEntries(map: DstiCooldownMap, now: number): DstiCooldownMap {
    const cleaned: DstiCooldownMap = {}

    Object.keys(map).forEach((uuid) => {
      const ts = map[uuid]

      if (!ts) return

      if (now - ts <= DSTI_COOLDOWN_MS + 1000) {
        cleaned[uuid] = ts
      }
    })

    return cleaned
  }
}
